// src/pages/outsourcing_management/InquirySourceLines.tsx
//
// The requisition lines behind an Inquiry's lines (EE-253 P2b,
// INQUIRY_ITEM_ALLOCATIONS). Until the Inquiry is sent to a supplier (Draft,
// or Approved and not sent) the buyer can take a line out - it goes back to
// Awaiting Inquiry - or change the lines in the compose dialog.
//
// P3: each line's share of the Inquiry line's price and of the transport,
// once the prices are in, and - on a raw-material line bought in stock
// lengths - how far the lengths checked over so far cover it, in mm.
import { FunctionComponent, useContext, useState } from "react";
import AppLink from "./AppLink";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { InquiryDetailType, InquiryStatusEnum } from "types/InquiryTypes";
import { useReleaseEnquiryLine } from "hooks/useRequisitionPool";
import { reformatDate } from "util/DateUtil";
import { formatCentsOrDash } from "util/money";
import { formatAmount } from "util/TextUtil";
import EnquiryComposeDialog, { poolLinesOf } from "./EnquiryComposeDialog";

type PropsType = {
  inquiry: InquiryDetailType;
  onChanged: () => void;
  onNavigate: () => void;
};

const HEADERS = [
  "Line",
  "Requisition / line",
  "SO",
  "Part",
  "Required",
  "Required by",
  "Ready to use",
  "Cost share",
  "Transport share",
  "",
];

const InquirySourceLines: FunctionComponent<PropsType> = ({ inquiry, onChanged, onNavigate }) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const releaseMutation = useReleaseEnquiryLine(token);
  const [composing, setComposing] = useState(false);
  // Still collecting lines: the supplier has not been asked yet.
  const isDraft =
    inquiry.state === InquiryStatusEnum.DRAFT ||
    (inquiry.state === InquiryStatusEnum.APPROVED && !inquiry.sentAt);
  const rows = inquiry.items.flatMap((item, i) =>
    (item.sources ?? []).map((src) => ({ src, item, itemNo: i + 1 }))
  );
  if (!rows.length) return null;
  const liveCount = rows.filter(({ src }) => src.live).length;

  const release = (requisitionItemId: number) =>
    releaseMutation.mutate(
      { inquiryId: inquiry.id, requisitionItemId },
      {
        onSuccess: () => {
          addToast({ type: "success", message: "Line back in Awaiting Inquiry" });
          onChanged();
        },
        onError: (err: any) =>
          addToast({ type: "error", message: String(err?.message ?? err), keep: true }),
      }
    );

  return (
    <Box sx={{ mt: 2 }} data-testid="inquiry-source-lines">
      <EnquiryComposeDialog
        open={composing}
        inquiryId={inquiry.id}
        lines={composing ? poolLinesOf(inquiry) : []}
        onClose={(saved) => {
          setComposing(false);
          if (saved !== undefined) onChanged();
        }}
      />
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Requisition lines ({rows.length})
        </Typography>
        {isDraft && (
          <Button size="small" variant="outlined" onClick={() => setComposing(true)}>
            Edit requisition lines
          </Button>
        )}
      </Box>
      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {HEADERS.map((h) => (
                <TableCell key={h} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ src, item, itemNo }) => {
              // A line bought in stock lengths serves several requisition
              // lines: the server shares the lengths in out by need date, in
              // mm. Any other line has one source, and its goods-in is the
              // line's own.
              const mm = src.coverage != null;
              const need = mm ? src.coverage?.needMm ?? 0 : item.quantity ?? 0;
              const got = mm ? src.coverage?.gotMm ?? 0 : item.readyToUseQty ?? 0;
              const done = mm ? !!src.coverage?.ready : !!need && got >= need;
              return (
                <TableRow
                  key={src.key}
                  sx={src.cancelled || !src.live ? { opacity: 0.5 } : undefined}
                  data-testid={`inquiry-source-${src.requisitionItemId}`}
                >
                  <TableCell>{itemNo}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <AppLink to={`/requisitions/${src.requisitionId}`} onClick={onNavigate}>
                      {src.requisitionNo}
                    </AppLink>{" "}
                    / L{src.lineNo ?? "?"}
                  </TableCell>
                  <TableCell>{src.salesOrderNo ?? "-"}</TableCell>
                  <TableCell>{item.product?.name ?? item.comment ?? "-"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{src.required || "-"}</TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      color:
                        inquiry.deliveryDate && src.requiredBy && src.requiredBy < inquiry.deliveryDate
                          ? "warning.main"
                          : undefined,
                    }}
                  >
                    {src.requiredBy ? reformatDate(src.requiredBy) : "-"}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    {src.cancelled ? (
                      "Cancelled"
                    ) : (
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={done ? 100 : need ? Math.min(100, (got / need) * 100) : 0}
                          color={done ? "success" : "primary"}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-testid={`inquiry-source-ready-${src.requisitionItemId}`}
                        >
                          {mm && src.coverage?.needMm == null
                            ? done
                              ? "All lengths in"
                              : "Waiting for every length"
                            : `${formatAmount(got)} / ${formatAmount(need)}${mm ? " mm" : ""}`}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: "nowrap" }}
                    data-testid={`inquiry-source-cost-${src.requisitionItemId}`}
                  >
                    {formatCentsOrDash(src.costShareCents)}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: "nowrap" }}
                    data-testid={`inquiry-source-transport-${src.requisitionItemId}`}
                  >
                    {formatCentsOrDash(src.transportShareCents)}
                  </TableCell>
                  <TableCell>
                    {isDraft && src.live && liveCount > 1 && (
                      <IconButton
                        size="small"
                        title="Take out - back to Awaiting Inquiry"
                        disabled={releaseMutation.isLoading}
                        onClick={() => release(src.requisitionItemId)}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default InquirySourceLines;
